import { getImageAspectRatio } from "./utils";

export const loadImages = async (
  stageRef,
  setImages,
  showLoading,
  hideLoading,
  setIsLoadingComplete,
  navigate,
  showAlert
) => {
  showLoading();
  const bodies = JSON.parse(sessionStorage.getItem("selectedBodies")) || [];
  const faces = JSON.parse(sessionStorage.getItem("selectedFaces")) || [];

  // try to load images from session storage
  const faceUrls = JSON.parse(
    sessionStorage.getItem("faceImageBlobUrls") || "{}"
  );
  const bodyUrls = JSON.parse(
    sessionStorage.getItem("bodyImageBlobUrls") || "{}"
  );

  const stage = stageRef.current;
  const canvasHeight = stage.height();
  const canvasWidthLimit = window.innerWidth;

  // finalBodyHeight: rendered height of the body image in the canvas.
  // Reduced from canvas × 0.975 to canvas × 0.92 to give the face 1.2x more
  // "head room" — the empty space between the canvas top and the body's neck.
  //
  // Why: when the default face was scaled up by 1.2x (per client request,
  // see face block below), the face became too large relative to the empty
  // area above the neck where it lives by default. Shrinking the body in
  // canvas expands the empty space above it, restoring the original
  // face / head-room proportion.
  //
  // Math: head room = empty above body image + transparent inside body image.
  // Body images are normalized server-side (BodyNeckMarker → 1075×W with
  // neck at 860 from bottom), so the transparent portion above the neck
  // is always 215/1075 = 20% of the body image height.
  //   Old (0.975): 0.025 + 0.975 × 0.20 = 22.0% of canvas → too tight at face × 1.2
  //   New (0.92):  0.080 + 0.920 × 0.20 = 26.4% of canvas → 22.0% × 1.2 ✓
  //
  // Side effect: body content (the visible character) shrinks ~5% in
  // canvas — imperceptible in practice.
  let finalBodyHeight = canvasHeight * 0.92;

  let totalBodiesWidth = 0;
  let totalFacesWidth = 0;
  const bodyWidths = [];
  const faceWidths = [];

  const allImages = await Promise.all(
    bodies.concat(faces).map(async (imagePath, index) => {
      const isBody = bodies.includes(imagePath);
      const isFace = faces.includes(imagePath);
      let height;
      let width;
      try {
        // try to load image from session storage
        let imageData = null;

        if (isBody && bodyUrls[imagePath]) {
          // if it's a body and the image exists in session storage
          imageData = bodyUrls[imagePath];
        } else if (isFace && faceUrls[imagePath]) {
          // if it's a face and the image exists in session storage
          imageData = faceUrls[imagePath];
        } else {
          console.warn(
            `Image ${imagePath} not found in SessionStorage, trying IndexedDB`
          );
        }

        if (!imageData) {
          throw new Error(`Image not found for ${imagePath}`);
        }

        const dimensions = await getImageAspectRatio(imageData);
        const aspectRatio = dimensions.aspectRatio;
        const imgHeight = dimensions.height;

        if (isBody) {
          height = finalBodyHeight;
          width = height * aspectRatio;
          bodyWidths[index] = width;
          totalBodiesWidth += width;
        } else if (isFace) {
          const fileName = imagePath.split("/").pop();

          // Default head size calculation. Each parameter:
          //
          //   faceHeightPercentage: integer encoded in the face filename
          //     (e.g. "+425" means the actual face occupies 42.5% of the image
          //     height — the rest of the image is hair, neck, and transparent
          //     padding around the head). Set by the server during face
          //     detection.
          //   /1000: converts the integer (e.g. 425) to a ratio (0.425).
          //   faceHeight: actual face pixel height inside the loaded image.
          //
          //   finalBodyHeight: 92% of canvas height (see comment at its
          //     definition above for why 0.92 instead of the original 0.975).
          //   13/16 (≈0.8125): "effective body" portion used to calibrate the
          //     face against — excludes the lower ~19% of the body
          //     (legs/feet area) where proportions don't matter for face sizing.
          //   5.25: empirical face-to-effective-body ratio — face occupies
          //     1/5.25 of the effective body height to look naturally
          //     proportioned.
          //   1.2: client-requested 20% boost to default face size, so heads
          //     appear larger by default before any user editing.
          //   (0.975 / 0.92) ≈ 1.06: compensation factor for finalBodyHeight
          //     being reduced from 0.975 to 0.92 of canvas (see comment at
          //     its definition above). Without this, the face would shrink
          //     proportionally too (since face = f(finalBodyHeight)) and the
          //     1.2x boost would be partially undone. With it, the face
          //     stays at exactly 1.2x of its original size while only the
          //     body display shrinks.
          //
          //   sf (scale factor): applied to the ENTIRE face image (not just
          //     the face pixels), so when the image is rescaled the face
          //     inside it reaches the required size while preserving internal
          //     proportions (hair, neck, padding).
          const faceHeightPercentage = parseInt(
            fileName.split("+")[1].split(".")[0]
          );
          const faceHeightRatio = faceHeightPercentage / 1000;
          const faceHeight = imgHeight * faceHeightRatio;

          const requiredFaceH = ((finalBodyHeight * (13 / 16)) / 5.25) * 1.2 * (0.975 / 0.92);
          const sf = requiredFaceH / faceHeight;

          height = imgHeight * sf;
          width = height * aspectRatio;
          faceWidths.push(width);
          totalFacesWidth += width;
        }

        return {
          src: imageData,
          width: width,
          height: height,
          aspectRatio: aspectRatio,
          flipped: false,
          originalSrc: imagePath,
          position: { x: 0, y: 0 },
          draggable: !isBody,
        };
      } catch (error) {
        console.error("Error loading image:", error);
        const message = "נראה שתמונה זו כבר הייתה בשימוש, אנא נסו שנית";
        showLoading();
        showAlert("err", message, 10000, 6000);
        window.location.reload();
        return null;
      }
    })
  );

  const validImages = allImages.filter((image) => image !== null);

  const totalBodiesWidthReq = totalBodiesWidth * 1.1;
  let canvasWidth = Math.min(totalBodiesWidthReq, canvasWidthLimit);

  let scaleFactor = 1;
  if (totalBodiesWidthReq > canvasWidthLimit) {
    scaleFactor *= canvasWidthLimit / totalBodiesWidthReq;
    finalBodyHeight *= scaleFactor;
    bodyWidths.forEach((width, index) => {
      bodyWidths[index] = width * scaleFactor;
    });
  }

  const totalBodyGaps = bodies.length;
  const bodyGapWidth =
    Math.abs(canvasWidth - totalBodiesWidth * scaleFactor) / totalBodyGaps;

  let currentX = bodyGapWidth / 2;
  const positionedBodies = validImages.map((image, index) => {
    if (bodies.includes(image.originalSrc)) {
      const width = bodyWidths[index];
      const height = width / image.aspectRatio;
      const newImage = {
        ...image,
        width: width,
        height: height,
        position: { x: currentX, y: canvasHeight - height },
      };
      currentX += width + bodyGapWidth;
      return newImage;
    }
    return image;
  });

  const totalFaceGaps = faces.length + 1;
  const faceGapWidth = (canvasWidth - totalFacesWidth) / totalFaceGaps;

  currentX = faceGapWidth;

  const positionedFaces = positionedBodies.map((image) => {
    if (faces.includes(image.originalSrc)) {
      const newImage = {
        ...image,
        width: image.width * scaleFactor,
        height: image.height * scaleFactor,
        position: { x: currentX, y: 0 },
      };
      currentX += newImage.width + faceGapWidth;
      return newImage;
    }
    return image;
  });

  setImages(positionedFaces);
  hideLoading();
  setIsLoadingComplete(true);
  return canvasWidth;
};

export const handleSaveAndFinish = async (
  stageRef,
  setActiveImage,
  navigate,
  eventName
) => {
  if (stageRef.current) {
    setActiveImage(null);

    setTimeout(async () => {
      let dataUrl = null;

      // A stale ratio from a previous save must never be reported for THIS one
      try {
        sessionStorage.removeItem("finalImagePixelRatio");
      } catch (ignored) {
        /* telemetry only */
      }

      for (let pixelRatio = 3; pixelRatio >= 1; pixelRatio -= 0.1) {
        try {
          dataUrl = stageRef.current.toDataURL({
            pixelRatio: pixelRatio,
            imageSmoothingEnabled: true,
          });
          if (dataUrl.length < 2.75 * 1024 * 1024) {
            // The size loop silently degrades print quality (3 -> 1) and nobody
            // measures how often — record the chosen ratio for the
            // approve-timing report. Telemetry only, never blocks the flow.
            try {
              sessionStorage.setItem(
                "finalImagePixelRatio",
                String(Math.round(pixelRatio * 10) / 10)
              );
            } catch (ignored) {
              /* telemetry only */
            }
            break;
          }
        } catch (e) {
          console.error("Error generating image:", e);
        }
      }

      if (dataUrl) {
        localStorage.setItem("finalImage", dataUrl);
        navigate(`/${eventName}/result`);
        return;
      }
    }, 1000);
  }
};
